import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useQuill } from "react-quilljs";
import _ from "lodash";
import { useTheme } from "@mui/material/styles";
import "quill/dist/quill.snow.css";

interface FormikProps {
  values: {
    content: string;
  };
  setFieldValue: (field: string, value: any) => void;
}

export const OcQuillToolbar = ({ formik }: { formik: FormikProps }) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const saveLabel = t("OC_SAVE");

  const { quill, quillRef } = useQuill({
    modules: {
      toolbar: "#toolbar",
      clipboard: {
        matchVisual: true,
      },
    },
    formats: [
      "size",
      "bold",
      "script",
      "italic",
      "underline",
      "strike",
      "color",
      "background",
      "list",
      "link",
      "image",
      "video",
      "indent",
    ],
  });

  useEffect(() => {
    if (quill) {
      if (!_.isEqual(formik.values.content, quill.root.innerHTML)) {
        quill.clipboard.dangerouslyPasteHTML(0, formik.values.content);
      }

      const style = document.createElement("style");
      style.innerHTML = `
        .ql-snow .ql-tooltip.ql-editing a.ql-action::after {
          content: '${saveLabel}';
        }
        .ql-snow .ql-tooltip a.ql-remove::before{
          content: '${t("OC_DELETE")}';
        }
        .ql-snow .ql-tooltip a.ql-action::after {
          content: '${t("MY_NEWS_EDITING")}';
        }
      `;
      document.head.appendChild(style);

      quill.on("text-change", () => {
        formik.setFieldValue("content", quill.root.innerHTML);
      });

      const tooltipSelector = '.ql-tooltip[data-mode="video"]';
      quill.on("selection-change", () => {
        const videoInput = document.querySelector(
          `${tooltipSelector} input[type="text"]`,
        );
        if (videoInput) {
          (videoInput as HTMLInputElement).placeholder = t("MY_NEWS_EMBED_VIDEO");
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quill, t, saveLabel]);

  const toolbarRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (toolbarRef.current) return;

    const toolbar = document.getElementById("toolbar");
    toolbarRef.current = toolbar;

    if (toolbar) {
      toolbar.innerHTML = "";

      const colorSelect = document.createElement("select");
      colorSelect.className = "ql-color";
      const colorOptions = [
        { value: theme.palette.greenBase.main },
        { value: theme.palette.yellowBase.main },
        { value: theme.palette.blueBase.main },
        { value: theme.palette.primary.main },
        { value: theme.palette.white.main },
        { value: theme.palette.darkBase.main },
      ];
      colorOptions.forEach((option) => {
        const optionElement = document.createElement("option");
        optionElement.value = option.value;
        optionElement.textContent = option.value; // Use 'value' as fallback since 'label' does not exist
        colorSelect.appendChild(optionElement);
      });

      const backgroundSelect = document.createElement("select");
      backgroundSelect.className = "ql-background";
      const backgroundOptions = [
        { value: theme.palette.greenBase.main },
        { value: theme.palette.yellowBase.main },
        { value: theme.palette.blueBase.main },
        { value: theme.palette.primary.main },
        { value: theme.palette.white.main },
        { value: theme.palette.darkBase.main },
      ];
      backgroundOptions.forEach((option) => {
        const optionElement = document.createElement("option");
        optionElement.value = option.value;
        optionElement.textContent = option.value;
        backgroundSelect.appendChild(optionElement);
      });

      const sizeSelect = document.createElement("select");
      sizeSelect.className = "ql-size";
      const customSizeOptions = [
        { value: "small", label: t("MY_NEWS_SMALL") },
        { value: "medium", label: t("MY_NEWS_MEDIUM") },
        { value: "large", label: t("MY_NEWS_LARGE") },
        { value: "huge", label: t("MY_NEWS_HUGE") },
      ];
      customSizeOptions.forEach((option) => {
        const optionElement = document.createElement("option");
        optionElement.value = option.value;
        optionElement.textContent = option.label;
        sizeSelect.appendChild(optionElement);
      });

      const indentSelect = document.createElement("select");
      indentSelect.className = "ql-indent";
      const indentOptions = [
        { value: "1", label: t("INDENT_1") },
        { value: "2", label: t("INDENT_2") },
        { value: "3", label: t("INDENT_3") },
      ];
      indentOptions.forEach((option) => {
        const optionElement = document.createElement("option");
        optionElement.value = option.value;
        optionElement.textContent = option.label;
        indentSelect.appendChild(optionElement);
      });

      toolbar.appendChild(sizeSelect);

      const toolbarButtons = [
        { className: "ql-bold" },
        { className: "ql-italic" },
        { className: "ql-underline" },
        { className: "ql-strike" },
        colorSelect,
        backgroundSelect,
        { className: "ql-list", value: "ordered" },
        { className: "ql-list", value: "bullet" },
        { className: "ql-indent", value: "-1" },
        { className: "ql-indent", value: "+1" },
        { className: "ql-link" },
        { className: "ql-image" },
        { className: "ql-video" },
      ];

      toolbarButtons.forEach((button) => {
        const buttonElement = document.createElement("button");
        buttonElement.className = button.className;
        if (button.value) {
          buttonElement.setAttribute("value", button.value);
        }
        if (button instanceof HTMLElement) {
          toolbar.appendChild(button);
        } else {
          toolbar.appendChild(buttonElement);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div>
      <div
        id="toolbar"
        style={{ ["--save-label" as any]: `'${saveLabel}'` }}
      ></div>
      <div ref={quillRef} />
    </div>
  );
};

export default OcQuillToolbar;
